import * as opt from "lib/opt"
import * as util from "lib/util"

// Store references to native methods we'll use internally
const nativeArrayMap = Array.prototype.map
const nativeArrayFilter = Array.prototype.filter
const nativeArrayReduce = Array.prototype.reduce
const nativeArraySome = Array.prototype.some
const nativeArrayEvery = Array.prototype.every
const nativeArrayFind = Array.prototype.find
const nativeArrayFindIndex = Array.prototype.findIndex
const nativeArrayJoin = Array.prototype.join

// Vec (Array) implementations
Array.prototype.First = function <Arr extends readonly unknown[]>(
	this: Arr
): Opt<ElementAt<Arr, 0>> {
	return (this.length === 0 ? opt.None() : opt.Some(this[0])) as Opt<ElementAt<Arr, 0>>
}

Array.prototype.Sort = Array.prototype.sort!

// Slice methods - use saved native methods internally
Array.prototype.Map = function <T, U>(this: T[], fn: (element: T, index: int) => U): U[] {
	return (nativeArrayMap as any).call(this, fn)
}

Array.prototype.Filter = function <T>(this: T[], fn: (element: T, index: int) => bool): T[] {
	return (nativeArrayFilter as any).call(this, fn)
}

Array.prototype.Append = function <T>(this: T[], ...items: T[]): T[] {
	for (const item of items) {
		if (Array.isArray(item)) {
			this.push!(...item)
		} else {
			this.push!(item)
		}
	}
	return this
}

Array.prototype.Skip = function <T>(this: T[], n: int): T[] {
	return this.slice(n)
}

Array.prototype.Take = function <T>(this: T[], n: int): T[] {
	return this.slice(0, n)
}

Array.prototype.FlatMap = function <T, U>(this: T[], fn: (element: T) => U[]): U[] {
	return (nativeArrayMap as any).call(this, fn).flat()
}

Array.prototype.FilterMap = function <T, U>(this: T[], fn: (element: T) => opt.Opt<U>): U[] {
	return (nativeArrayFilter as any)
		.call(this, (el: T) => fn(el).IsSome())
		.map((el: T) => fn(el).Expect("FilterMap"))
}

Array.prototype.Fold = function <T, U>(this: T[], initial: U, folder: (acc: U, item: T) => U): U {
	return (nativeArrayReduce as any).call(this, folder as any, initial) as U
}

Array.prototype.FromEntries = function <K extends PropertyKey, V>(this: [K, V][]): Record<K, V> {
	return Object.fromEntries(this) as Record<K, V>
}

Array.prototype.Copy = function <T>(this: T[]): T[] {
	return [...this]
}

Array.prototype.Position = function <T>(this: T[], predicate: (item: T) => bool): opt.Opt<int> {
	const index = (nativeArrayFindIndex as any).call(this, predicate)
	return opt.Some(index).Filter((i) => i >= 0)
}

Array.prototype.Any = function <T>(this: T[], predicate: (item: T) => bool): bool {
	return (nativeArraySome as any).call(this, predicate)
}

Array.prototype.All = Array.prototype.every!

Array.prototype.Find = function <T>(this: T[], predicate: (item: T) => bool): opt.Opt<T> {
	return opt.FromNullable((nativeArrayFind as any).call(this, predicate))
}

Array.prototype.GroupBy = function <T, K extends string>(
	this: T[],
	keyFn: (item: T) => K
): Record<K, T[]> {
	const result = {} as Record<K, T[]>
	for (const item of this) {
		const key = keyFn(item)
		if (!result[key]) {
			result[key] = []
		}
		result[key].push!(item)
	}
	return result
}

const defaultComparator = <T>(a: T, b: T) => (a > b ? 1 : -1)

Array.prototype.Max = function <T>(this: T[], comparator?: (a: T, b: T) => int): opt.Opt<T> {
	const comp = comparator || defaultComparator
	if (this.length === 0) {
		return opt.None()
	}
	return opt.Some(this.Fold(this[0], (a, b) => (comp(a, b) > 0 ? a : b)))
}

Array.prototype.Min = function <T>(this: T[], comparator?: (a: T, b: T) => int): opt.Opt<T> {
	const comp = comparator || defaultComparator
	if (this.length === 0) {
		return opt.None()
	}
	return opt.Some(this.Fold(this[0], (a, b) => (comp(a, b) < 0 ? a : b)))
}

Array.prototype.Range = function <T>(this: T[], fn: (index: int, item: T) => void | bool): void {
	for (let i = 0; i < this.length; i++) {
		if (fn(i, this[i]) === false) {
			break
		}
	}
}

Array.prototype.Sum = function <T extends int>(this: T[]): T extends int ? T : never {
	return this.Fold(0 as any, (acc, val) => (acc + val) as any)
}

Array.prototype.Reduce = function <T>(this: T[], reducer: (acc: T, item: T) => T): opt.Opt<T> {
	if (this.length === 0) {
		return opt.None<T>()
	}
	return opt.Some(
		(nativeArrayReduce as any).call(this.slice(1), reducer as any, this[0])
	) as opt.Opt<T>
}

Array.prototype.SumBy = function <T>(this: T[], fn: (item: T) => int): int {
	return this.Fold(0, (acc, item) => acc + fn(item))
}

Array.prototype.Join = function <T>(this: T[], separator?: string): string {
	return (nativeArrayJoin as any).call(this, separator)
}

Array.prototype.MeanBy = function <T>(this: T[], fn: (item: T) => int): int {
	if (this.length === 0) return 0
	return this.SumBy(fn) / this.length
}

Array.prototype.Mean = function <T extends int>(this: T[]): int {
	if (this.length === 0) return 0
	return this.Sum() / this.length
}

Array.prototype.Last = function <T>(this: T[]): opt.Opt<T> {
	return this.IsEmpty() ? opt.None() : opt.Some(this[this.length - 1])
}

Array.prototype.Clear = function <T>(this: T[]): void {
	this.length = 0
}

Array.prototype.Contains = Array.prototype.includes!

Array.prototype.Remove = function <T>(this: T[], index: int): opt.Opt<T> {
	if (index < 0 || index >= this.length) {
		return opt.None()
	}
	return opt.Some(this.splice(index, 1)[0])
}

Array.prototype.Insert = function <T>(this: T[], index: int, element: T): void {
	this.splice(index, 0, element)
}

Array.prototype.Trucate = function <T>(this: T[], len: int): void {
	if (len < this.length) {
		this.length = len
	}
}

Array.prototype.Resize = function <T>(this: T[], newLen: int, value: T): void {
	if (newLen > this.length) {
		this.push!(...Array(newLen - this.length).fill(value))
	} else {
		this.length = newLen
	}
}

Array.prototype.SplitOff = function <T>(this: T[], at: int): T[] {
	return this.splice(at)
}

Array.prototype.Drain = function <T>(this: T[], start?: int, end?: int): T[] {
	return this.splice(start ?? 0, end != null ? end - (start ?? 0) : this.length)
}

Array.prototype.Retain = function <T>(this: T[], f: (element: T) => bool): void {
	for (let i = this.length - 1; i >= 0; i--) {
		if (!f(this[i])) {
			this.splice(i, 1)
		}
	}
}

Array.prototype.Dedup = function <T>(this: T[]): void {
	for (let i = 1; i < this.length; i++) {
		if (this[i] === this[i - 1]) {
			this.splice(i, 1)
			i--
		}
	}
}

Array.prototype.Get = function <T>(this: T[], index: int): opt.Opt<T> {
	return index < 0 || index >= this.length ? opt.None() : opt.Some(this[index])
}

Array.prototype.Len = function <T>(this: T[]): int {
	return this.length
}

Array.prototype.IsEmpty = function <T>(this: T[]): bool {
	return util.IsEmpty(this)
}

Array.prototype.DedupBy = function <T>(this: T[], f: (a: T, b: T) => bool): void {
	for (let i = 1; i < this.length; i++) {
		if (f(this[i], this[i - 1])) {
			this.splice(i, 1)
			i--
		}
	}
}

// Tuple implementations
Array.prototype.Second = function <T extends readonly [unknown, unknown]>(
	this: T
): opt.Opt<ElementAt<T, 1>> {
	return this.Nth(1)
}

Array.prototype.Nth = function <I extends int, Arr extends readonly unknown[]>(
	this: Arr,
	n: I
): opt.Opt<ElementAt<Arr, I>> {
	if (n < 0 || n >= this.length) {
		return opt.None()
	}
	return opt.Some(this[n] as ElementAt<Arr, I>)
}

Array.prototype.Swap = function <A, B>(this: readonly [A, B]): [B, A] {
	if (this.length !== 2) {
		log.Fatal("attempt to swap non-2-tuple")
	}
	return [this[1], this[0]]
}

Array.prototype.Into = function <A, B>(this: readonly [A, B]): [A, B] {
	if (this.length !== 2) {
		log.Fatal("attempt to convert non-2-tuple")
	}
	return [this[0], this[1]]
}

Array.prototype.Split = function <A, B>(this: readonly [A, B]): [A, B] {
	if (this.length !== 2) {
		log.Fatal("attempt to split non-2-tuple")
	}
	return [this[0], this[1]]
}
